import type { DateRange } from "@repo/app-types/entities/filters";
import type { WinRateTrendData } from "@repo/app-types/entities/topic";
import { useMemo, type FC } from "react";
import { MetricTrendCard, type MetricTrendPoint } from "./metricTrendCard.js";

interface WinRateTrendCardProps {
  data: WinRateTrendData | undefined;
  isLoading: boolean;
  dateRange: DateRange;
}

/**
 * Render a 0-1 rate as an integer percent (`62 %` → `"62%"`).
 * Matches the {@link OutcomeRatePill} formatting used elsewhere so
 * the same number reads the same way across the page.
 */
function formatRate(rate: number | null): string {
  if (rate === null) return "—";
  return `${Math.round(rate * 100)}%`;
}

/**
 * Topics overview win-rate card. Renders the period-aggregate win
 * rate over the visible date range, a delta chip vs the immediately
 * preceding equal-length window, and a 7-day rolling sparkline. The
 * diff is in percentage points (e.g. `+4pp`); the headline is rounded
 * to whole percents to match the rest of the app's win-rate convention.
 *
 * Hidden by the parent when the active playbook has no outcome
 * definition with a green value — the server returns the empty
 * payload sentinel and the parent collapses the hero to one column.
 */
export const WinRateTrendCard: FC<WinRateTrendCardProps> = ({
  data,
  isLoading,
  dateRange,
}) => {
  const { points, currentRate, priorRate, hasData } = useMemo(() => {
    if (!data) {
      return {
        points: [] as MetricTrendPoint[],
        currentRate: null,
        priorRate: null,
        hasData: false,
      };
    }
    const pts: MetricTrendPoint[] = data.buckets.map((b) => ({
      date: b.date,
      value: b.rollingWinRate,
    }));
    return {
      points: pts,
      currentRate: data.currentPeriodWinRate,
      priorRate: data.priorPeriodWinRate,
      hasData: data.currentPeriodWinRate !== null,
    };
  }, [data]);

  // Delta on the headline metric is in percentage points. Round to
  // whole pp so the pill reads cleanly next to the integer-percent
  // headline (the underlying rates may be fractional).
  const diff =
    currentRate !== null && priorRate !== null
      ? Math.round((currentRate - priorRate) * 100)
      : null;

  return (
    <MetricTrendCard
      label="Overall win rate"
      headlineValue={formatRate(currentRate)}
      diff={diff}
      formatDelta={(d) => `${d > 0 ? "+" : ""}${d}pp`}
      points={points}
      yDomain={[0, 1]}
      formatTooltipValue={formatRate}
      isLoading={isLoading && !data}
      hasNoData={!hasData}
      dateRange={dateRange}
    />
  );
};
